<script setup lang="ts">
import { onMounted, onBeforeUnmount, computed, defineAsyncComponent, ref, nextTick, watch } from 'vue';
import { RouterView, useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { usePlayerStore } from '@/stores/player';
import { useFiltersStore, clampMiniPlayer } from '@/stores/filters';
import { useChannelListStore } from '@/stores/channelList';
import { useSettingsStore } from '@/stores/settings';
import { usePreferencesStore } from '@/stores/preferences';
import { log } from '@/lib/log';
import { disarm as disarmEventAutoAdvance, reportEventPlaybackFailure } from '@/composables/useEventAutoAdvance';
import { stopEpgNotificationScheduler, useEpgNotifications } from '@/composables/useEpgNotifications';
import { useToast } from '@/composables/useToast';
import type { PlayerError } from '@/lib/player/playerErrors';
import {
    canOfferRetry,
    fromSessionAcquisitionStatus,
    playbackErrorToastMessage,
} from '@/lib/player/playerErrors';
import { isSessionBackedLiveMode, retuneActionForErrorKind } from '@/lib/player/retunePolicy';
import { isStreamLimitError } from '@/lib/player/streamLimitRetry';
import { isDockedBarTier } from '@/lib/layoutTier';
import { useLayoutTier } from '@/composables/useLayoutTier';
import { api, ApiError } from '@/lib/api';
import { formatUserError } from '@/lib/userMessages';
import { loginDestination } from '@/lib/loginRedirect';
import type { Channel } from '@/stores/channelList';
import AppRail from '@/components/layout/AppRail.vue';
import BottomTabBar from '@/components/layout/BottomTabBar.vue';
import ToastStack from '@/components/primitives/ToastStack.vue';
import ConfirmDialogHost from '@/components/primitives/ConfirmDialogHost.vue';
import KeyboardShortcuts from '@/components/primitives/KeyboardShortcuts.vue';

const NodecastPlayer = defineAsyncComponent(() => import('@/components/player/NodecastPlayer.vue'));

const auth = useAuthStore();
const route = useRoute();
const router = useRouter();
const playerStore = usePlayerStore();
const filters = useFiltersStore();
const channelStore = useChannelListStore();
const settingsStore = useSettingsStore();
const prefsStore = usePreferencesStore();
const toast = useToast();
const livePlaybackFailure = ref('');
const livePlaybackRetryAvailable = ref(true);

watch(() => playerStore.playTraceId, () => {
    livePlaybackFailure.value = '';
    livePlaybackRetryAvailable.value = true;
});
watch(
    () => playerStore.currentChannel
        ? `${playerStore.currentChannel.sourceId}:${playerStore.currentChannel.id}`
        : '',
    (next, previous) => {
        if (next === previous) return;
        livePlaybackFailure.value = '';
        livePlaybackRetryAvailable.value = true;
    },
);

// Spec 10 — EPG start notifications. The app shell is the ONLY place the
// scheduler can live and still fire while the user is on /movies, so this is
// the single `schedule: true` call site (the composable warns and refuses if
// a second one appears). It self-arms off the `epgNotificationsEnabled`
// setting + this browser's notification permission and is inert until both
// are satisfied — no timers, no fetches.
//
// `navigate` is the FALLBACK click path only, for when no service worker is
// active (Vite dev disables Serwist; a registration can also fail). With an
// active SW the notification is created via `registration.showNotification`
// and its click is delivered to the SW's own `notificationclick` handler,
// which focuses this tab and posts `epg-notification-click` — `main.ts` picks
// that up. Both paths resolve the destination through the same
// `notificationClickTarget()` helper.
useEpgNotifications({
    schedule: true,
    navigate: (path) => {
        void router.push(path).catch(() => undefined);
    },
});

// Live player error handler. On VM deployments, live full-video transcode is
// intentionally not an automatic fallback; the player already handles the cheap
// probe-driven audio-only repair path when H.264 video has unsupported audio.
//
// Session self-heal: the server's idle sweeper may reclaim a live playback
// session while the client is quiet (paused via blocked autoplay, hls.js
// wedged in stall-recovery) — the session URL then answers a terminal 403 and
// hls.js dies with a fatal network error. Re-tuning the same channel mints a
// fresh session. One attempt per channel per window so a genuinely dead stream
// still surfaces the error toast instead of looping.
//
// 2026-09-03 incident (plan Phase 0A): this was gated on `mode === 'rewind'`
// while transcode has the same terminal-expiry shape (its own 30-minute idle
// timeout, and since this phase the same `403 session_expired` status). The
// gate now asks retunePolicy which modes are session-backed. Phase 1b added
// `proxy` to that set once the stream route's registry liveness became
// authoritative; `remux` and `catchup` remain excluded — remux has no session
// identity of its own, and catchup is VOD-shaped, so recovery would silently
// jump to the live edge.
const LIVE_SESSION_RECOVERY_WINDOW_MS = 30000;
let lastLiveSessionRecovery = { key: '', at: 0 };
/** Key of the recovery retune currently in flight, or '' when none is. */
let liveSessionRecoveryInFlight = '';
function maybeRecoverLiveSession(err: PlayerError): boolean {
    // Live-buffer-resilience: WHICH kind gets auto-retuned (and how) is a
    // pure policy decision — see retunePolicy.ts for the mapping + the
    // 2026-07-05 incident rationale ('media-network' no longer force-
    // retunes).
    const action = retuneActionForErrorKind(err.kind);
    if (action === 'none') return false;
    const recoveringLimit = err.kind === 'stream-limit' && playerStore.mode !== 'catchup';
    if (!recoveringLimit && !isSessionBackedLiveMode(playerStore.streamResult?.mode ?? null)) return false;
    const ch = playerStore.currentChannel;
    if (!ch) return false;
    const key = `${ch.sourceId}:${ch.id}`;
    const now = Date.now();
    // A dead session URL can produce several fatal errors before the fresh
    // session lands (hls.js goes fatal on the FIRST 403, but Vidstack, the
    // provider-level listener and the stall watchdog can each surface one).
    // While our own retune is in flight they are the SAME event — consume
    // them so they neither start a second retune nor raise a toast about a
    // session that is already being replaced.
    if (liveSessionRecoveryInFlight === key) {
        log.debug('live.session_recover_duplicate', {
            sourceId: ch.sourceId,
            channelId: ch.id,
            kind: err.kind,
        });
        return true;
    }
    if (!recoveringLimit && lastLiveSessionRecovery.key === key && now - lastLiveSessionRecovery.at < LIVE_SESSION_RECOVERY_WINDOW_MS) {
        return false;
    }
    lastLiveSessionRecovery = { key, at: now };
    liveSessionRecoveryInFlight = key;
    const mode = playerStore.streamResult?.mode ?? null;
    log.info('live.session_recover', { sourceId: ch.sourceId, channelId: ch.id, kind: err.kind, action, mode });
    const recoveryResult = playerStore.streamResult;
    void (async () => {
        try {
            // A route change or a newer tune can take ownership during cleanup.
            if (playerStore.streamResult !== recoveryResult
                || playerStore.currentChannel?.sourceId !== ch.sourceId
                || playerStore.currentChannel?.id !== ch.id) return;
            // force: the whole point is re-tuning the channel that is "already
            // playing" — the same-channel no-op guard must not swallow this.
            await playerStore.selectChannel(ch, {
                force: true,
                attachmentReason: 'recovery',
                ...(recoveringLimit ? {
                    streamLimitRecovery: true,
                    streamLimitRecoveryRetryAfterSec: err.retryAfterSec,
                } : {}),
            });
        } catch (retuneErr) {
            // The old session is KNOWN dead (terminal 403 or provider
            // rejection) and the replacement did not arrive. Leaving the
            // dead result mounted kept a dead URL polling — the 2026-09-03
            // stale-session case. Drop it and tell the user what actually
            // stopped the retune (usually a provider limit); the error we
            // consumed only says the session ended, which explains nothing
            // about the failure the user is now looking at.
            const stillSame = playerStore.currentChannel?.sourceId === ch.sourceId
                && playerStore.currentChannel?.id === ch.id;
            if (stillSame) {
                playerStore.discardDeadStream('session_lost_recovery_failed');
                surfaceLiveSessionRecoveryFailure(retuneErr, ch);
            }
        } finally {
            if (liveSessionRecoveryInFlight === key) liveSessionRecoveryInFlight = '';
        }
    })();
    return true;
}

// Identical-fatal toast dedupe: a wedged stream can emit the same fatal error
// many times per second (each Vidstack recover round re-fails) — without this
// the user got a stack of 16 identical error toasts. This key is per-CHANNEL;
// the same message arriving from a DIFFERENT channel is collapsed one layer
// down, by `useToast` (field sweep B6).
const ERROR_TOAST_DEDUPE_MS = 10000;
let lastErrorToast = { key: '', at: 0 };

// Field sweep B6 — playback-error toasts used to be sticky (`timeoutMs: 0`),
// so a hopping session ended with four of them on screen naming channels the
// user had left minutes earlier. Long enough to read and to click the action,
// short enough that it cannot pile up.
const PLAYBACK_ERROR_TOAST_MS = 12000;
/**
 * Tag for toasts that describe the CURRENTLY tuned channel. Retired as soon as
 * the user tunes somewhere else — an error about a channel you already left is
 * noise, and the Phase 6 auto-advance walks up to five channels in a row.
 */
const PLAYBACK_ERROR_TOAST_SCOPE = 'live-playback-error';

function retirePlaybackErrorToasts(): void {
    toast.dismissScope(PLAYBACK_ERROR_TOAST_SCOPE);
    lastErrorToast = { key: '', at: 0 };
}

async function onLivePlaybackError(err: PlayerError): Promise<void> {
    if (!showPlayer.value) return;
    log.warn('live.playback_error', {
        kind: err.kind,
        fatal: err.fatal,
        mode: playerStore.streamResult?.mode ?? null,
    });
    if (!err.fatal) return;
    if (err.kind === 'stream-limit') disarmEventAutoAdvance('provider_limit');
    if (['media-decode', 'startup-no-frame', 'load-timeout'].includes(err.kind)) {
        disarmEventAutoAdvance('media_recovery');
    }
    const playbackId = playerStore.currentPlaybackId;
    if (playbackId && err.kind !== 'admin-stopped') {
        try {
            await api.playback.heartbeatLiveSession(playbackId);
        } catch (sessionError) {
            if (sessionError instanceof ApiError && sessionError.code === 'admin_force_stop') {
                if (playerStore.currentPlaybackId !== playbackId) return;
                disarmEventAutoAdvance('playback_stopped');
                await playerStore.stopPlayback('nodecast');
                toast.info('Playback stopped by an administrator.');
                return;
            }
        }
        // Ignore an old player error if a new tune replaced it while the
        // ownership check was in flight.
        if (playerStore.currentPlaybackId !== playbackId) return;
    }
    if (err.kind === 'startup-no-frame' || err.kind === 'load-timeout') {
        // The player already exhausted its one browser-local replacement.
        livePlaybackFailure.value = 'Live video did not start.';
        livePlaybackRetryAvailable.value = true;
    }
    // Phase 5: the live-position readout is no longer being maintained. Marked
    // BEFORE recovery is attempted, so the panel never keeps asserting a
    // position through a reacquisition; a successful retune clears it (a new
    // stream calls `resetLivePosition`) and so does the player resuming its
    // position reports (`setLivePosition`). The Phase 0B captures showed what
    // the old behaviour looked like: "At live edge" beside a dead 0:00 → 0:00
    // scrubber, after the session had ended.
    playerStore.markLivePositionUnknown();
    if (maybeRecoverLiveSession(err)) return;
    if (err.kind === 'admin-stopped') {
        // An explicit operator stop is not a failed event channel. Disarm the
        // still-armed mid-match fallback so it cannot immediately tune a new
        // channel and conceal the administrator's action.
        disarmEventAutoAdvance('playback_stopped');
    }
    // Arrived from an event with a ranked list? Step down it instead of asking
    // the user to go back to /events and search for the same fixture again.
    // Auto-advance owns the messaging from here, so no error toast — one per
    // failed channel while it walks the list would be noise, not information.
    if (!['admin-stopped', 'media-decode', 'startup-no-frame', 'load-timeout'].includes(err.kind) && reportEventPlaybackFailure()) return;
    if (!livePlaybackFailure.value) livePlaybackFailure.value = playbackErrorToastMessage(err);
    livePlaybackRetryAvailable.value = canOfferRetry(err.kind);
    // Field sweep B5 (fault 2) — the advice is chosen by error KIND, not by
    // `err.recoverable`. A provider stream-limit hop used to be told to use
    // the external-player button, which cannot help: the cap is on the
    // account upstream, so a native player hits the same wall.
    emitPlaybackToast(
        err.kind,
        playbackErrorToastMessage(err),
        playerStore.currentChannel,
        canOfferRetry(err.kind),
    );
}

/**
 * The one place a terminal live-playback message reaches the user. Shared by
 * the fatal-error path and by a failed session reacquisition so both obey the
 * same per-channel dedupe, scope, and action set.
 */
function emitPlaybackToast(
    dedupeKind: string,
    message: string,
    ch: Channel | null,
    allowRetry: boolean,
): void {
    // On the full Live stage the same failure is already persistent and
    // actionable in-player. Keep a toast only when that stage is hidden or
    // minimized, where the viewer cannot see the terminal panel.
    if (livePlaybackFailure.value && showPlayer.value && !isMini.value) return;
    const toastKey = `${dedupeKind}:${ch?.sourceId ?? ''}:${ch?.id ?? ''}`;
    const nowMs = Date.now();
    if (lastErrorToast.key === toastKey && nowMs - lastErrorToast.at < ERROR_TOAST_DEDUPE_MS) {
        return;
    }
    lastErrorToast = { key: toastKey, at: nowMs };
    // Atlas Phase 5 — failing-stream recovery (the Event Finder's headline case).
    // Offer to find the SAME event on another, more-reliable channel.
    const findElsewhereAction = ch
        ? {
              label: 'Find it elsewhere',
              run: () => {
                  void router.push({
                      path: '/events',
                      query: { failingSource: String(ch.sourceId), failingChannel: ch.id },
                  });
              },
          }
        : undefined;
    // Which kinds get a Retry button lives in playerErrors.ts as
    // `canOfferRetry`, with the per-kind rationale. It is NOT inlined here:
    // as an inline kind list it silently dropped the button when
    // 'provider-stalled' was carved out of 'media-network' (2026-08-24). The
    // caller passes that verdict in, so a non-kind surface (a failed session
    // reacquisition) can ask for Retry on its own terms.
    const retryAction = ch && allowRetry
        ? {
              label: 'Retry',
              run: () => {
                  void playerStore.selectChannel(ch, { force: true, attachmentReason: 'retry' }).catch(() => {
                      // Failure surfaces via the normal tune_failed → toast path.
                  });
              },
          }
        : undefined;
    toast.error(message, {
        timeoutMs: PLAYBACK_ERROR_TOAST_MS,
        scope: PLAYBACK_ERROR_TOAST_SCOPE,
        action: retryAction ?? findElsewhereAction,
        actions: retryAction && findElsewhereAction ? [findElsewhereAction] : undefined,
    });
    // playerStore.isSwitching is already cleared by NodecastPlayer itself.
}

/**
 * A terminal session loss was detected, ONE fresh session was requested, and
 * that request failed. The `session-lost` error was consumed by that attempt
 * and says only that the session ended, so the user must now be told what
 * actually blocked the replacement — in the 2026-09-03 incident that was a
 * provider stream limit, not anything about the expired session.
 *
 * Retry is offered for every cause EXCEPT the ones `canOfferRetry` rules out.
 * In practice that is `channel-offline`: the provider is serving a placeholder,
 * so another session request re-fetches the same placeholder. A provider limit
 * or an unclassified API failure both stay retryable — the remedy (free the
 * other stream, wait out a blip) is user-actionable — and an unclassified
 * failure defaults to offering Retry rather than stranding the viewer.
 */
function surfaceLiveSessionRecoveryFailure(retuneErr: unknown, ch: Channel): void {
    const status = retuneErr instanceof ApiError ? retuneErr.status : null;
    const code = retuneErr instanceof ApiError ? retuneErr.code : null;
    log.warn('live.session_recover_failed', {
        sourceId: ch.sourceId,
        channelId: ch.id,
        status,
        code,
    });
    // One phrasing per cause: a provider limit or an offline channel is named
    // by the player's own vocabulary (the same sentence a runtime limit shows),
    // and anything else falls back to the generic API-error formatter rather
    // than being guessed at.
    const classified = fromSessionAcquisitionStatus(isStreamLimitError(retuneErr) ? 509 : status);
    const message = classified
        ? playbackErrorToastMessage(classified)
        : formatUserError(retuneErr, { action: 'restart this channel' });
    livePlaybackFailure.value = message;
    livePlaybackRetryAvailable.value = classified ? canOfferRetry(classified.kind) : true;
    emitPlaybackToast(
        'session-recovery-failed',
        message,
        ch,
        classified ? canOfferRetry(classified.kind) : true,
    );
}

function retryLivePlayback(): void {
    const ch = playerStore.currentChannel;
    if (!ch) return;
    const channelKey = `${ch.sourceId}:${ch.id}`;
    livePlaybackFailure.value = '';
    livePlaybackRetryAvailable.value = true;
    void playerStore.selectChannel(ch, { force: true, attachmentReason: 'retry', fresh: true }).catch((err) => {
        if (`${playerStore.currentChannel?.sourceId}:${playerStore.currentChannel?.id}` !== channelKey) return;
        livePlaybackFailure.value = formatUserError(err, { action: 'retry this channel' });
        livePlaybackRetryAvailable.value = true;
    });
}

const isPublicRoute = computed(() => route.meta.public === true);
const isStandaloneRoute = computed(() => isPublicRoute.value || route.meta.standalone === true);
const isLiveRoute = computed(() => route.path === '/live');
/** `router/index.ts` `meta.fullBleed` — Live and Guide, the two canvas pages. */
const isFullBleedRoute = computed(() => route.meta.fullBleed === true);
const isWatchRoute = computed(() => route.path.startsWith('/watch'));

const showPlayer = computed(
    () =>
        !!playerStore.currentChannel
        // Keep the existing terminal Retry surface after recovery exhausts its
        // budget. Explicit Stop clears currentChannel and unmounts the player.
        && (playerStore.isPlaying || playerStore.isSwitching || !!livePlaybackFailure.value)
        && !isStandaloneRoute.value
        // /watch (VOD) mounts its own NodecastPlayer instance — hide the
        // live mini player there to avoid two simultaneous decoders.
        && !isWatchRoute.value,
);

// The shell owns live playback visibility, including starts for which the
// asynchronous player has not mounted or no stream result exists yet.
watch([isWatchRoute, isStandaloneRoute], ([watchRoute, standaloneRoute]) => {
    if (watchRoute || standaloneRoute) void playerStore.stopPlayback('nodecast', 'route_hidden');
}, { flush: 'sync' });

const isMini = computed(() => !isLiveRoute.value);

// ---------------------------------------------------------------
// Bounds tracking — replaces the previous Teleport-based mounting.
//
// Vidstack's <media-player> is a Lit web component; moving it
// between DOM parents (which Teleport does on `to` change) fires
// disconnectedCallback which destroys hls.js, resets the video
// element, and pauses playback. To keep playback continuous across
// route changes the player must NEVER move in the DOM. Instead we
// render it once at the App shell with `position: fixed` and
// animate its top/left/width/height between two states:
//
//   • on /live  — bounds match `#player-mount-live` (a placeholder
//                 div Live.vue puts in its grid cell). A
//                 ResizeObserver keeps the bounds in sync with the
//                 layout, so collapsing the channel sidebar or
//                 resizing the window updates the player's frame
//                 without animation jank.
//   • elsewhere — bounds = bottom-right tile sized by the user's
//                 stored `miniPlayerWidth/Height`.
//
// CSS `transition` on the player wrapper handles the slide.
// ---------------------------------------------------------------

const playerBounds = ref({ top: 0, left: 0, width: 360, height: 200 });
let liveResizeObserver: ResizeObserver | null = null;
let liveTargetEl: HTMLElement | null = null;

const MINI_MARGIN = 16; // px from the viewport edges in mini mode
const BOTTOM_NAV_PX = 56; // BottomTabBar height (matches --bottom-nav-height base)
const RAIL_WIDTH_COLLAPSED = 64; // matches --rail-width (desktop sidebar, collapsed)
const RAIL_WIDTH_EXPANDED = 220; // matches --rail-width-expanded (desktop sidebar, expanded)
const MINI_BAR_HEIGHT = 56; // 38px thumbnail + 8px vertical padding + border
const MINI_BAR_MARGIN_X = 10;
const MINI_BAR_NAV_GAP = 8;

// Atlas Phase 7 (open-q #6) — on the two touch tiers the mini player is a FIXED
// docked bar (phone: above the BottomTabBar; large-mobile: along the bottom of
// the content column, right of the rail); on desktop it stays the draggable
// bottom-right corner tile. The tier is the reactive gate — `window.innerWidth`
// is not reactive on its own, and `useLayoutTier` is the one place that answers
// "which layout is this" for the whole app (mobile-foldable-redesign §2.1).
const { tier } = useLayoutTier();
const isPhoneTier = computed(() => tier.value === 'phone');
const isMiniBar = computed<boolean>(() => isMini.value && isDockedBarTier(tier.value));

// Unified rail chrome — the AppRail sidebar is the navigation on every tier
// EXCEPT phone, where the BottomTabBar owns it and the column stays top-bar
// free. Below 1024 the rail is locked collapsed (see AppRail.vue); the desktop
// expanded preference is ignored there, not overwritten.
const showRail = computed(() => !isStandaloneRoute.value && !isPhoneTier.value);

// Atlas Phase 6 (open-q #7) — the snackbar must yield to the persistent PIP. The
// px the toast stack needs as its bottom offset to clear the mini tile/bar
// (0 when no mini player). Derived from the live mini bounds so it tracks
// resize/dock. Recomputes when playerBounds changes (onWindowResize).
const pipReserveBottom = computed<number>(() => {
    if (!showPlayer.value || !isMini.value) return 0;
    return Math.max(0, window.innerHeight - playerBounds.value.top + 8);
});

// Atlas Phase 6 — desktop PIP is draggable (mobile is a fixed dock, not).
// `miniPos` holds the user's dragged top-left (null = default bottom-right).
const miniPos = ref<{ top: number; left: number } | null>(null);
interface MiniDragState {
    startX: number;
    startY: number;
    startTop: number;
    startLeft: number;
    pointerId: number;
}
let miniDragState: MiniDragState | null = null;
function onMiniDragDown(e: PointerEvent): void {
    if (isMiniBar.value) return; // the mobile dock doesn't free-drag
    e.preventDefault();
    e.stopPropagation();
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);
    miniDragState = {
        startX: e.clientX,
        startY: e.clientY,
        startTop: playerBounds.value.top,
        startLeft: playerBounds.value.left,
        pointerId: e.pointerId,
    };
    target.addEventListener('pointermove', onMiniDragMove);
    target.addEventListener('pointerup', onMiniDragUp);
    target.addEventListener('pointercancel', onMiniDragUp);
}
function onMiniDragMove(e: PointerEvent): void {
    if (!miniDragState) return;
    const { width: w, height: h } = playerBounds.value;
    const nextLeft = clampMiniLeft(miniDragState.startLeft + (e.clientX - miniDragState.startX), w);
    const nextTop = clampMiniTop(miniDragState.startTop + (e.clientY - miniDragState.startY), h);
    miniPos.value = { top: nextTop, left: nextLeft };
    playerBounds.value = { ...playerBounds.value, top: nextTop, left: nextLeft };
}
function onMiniDragUp(e: PointerEvent): void {
    if (!miniDragState) return;
    const target = e.currentTarget as HTMLElement;
    try {
        target.releasePointerCapture(miniDragState.pointerId);
    } catch {
        /* capture may already be released */
    }
    target.removeEventListener('pointermove', onMiniDragMove);
    target.removeEventListener('pointerup', onMiniDragUp);
    target.removeEventListener('pointercancel', onMiniDragUp);
    miniDragState = null;
}

/**
 * The width the rail actually occupies right now — the left inset the docked
 * bar (large-mobile) and a dragged desktop tile must clear so neither can slide
 * under the fixed sidebar.
 *
 * EFFECTIVE, not preferred: below 1024 the rail is locked collapsed, so the
 * persisted `navExpanded` is deliberately not read there — reading it would put
 * a 220px reserve beside a 64px rail on a 752px screen. On phone there is no
 * rail at all (`showRail` is false), so the reserve is 0.
 */
function railReservePx(): number {
    if (tier.value === 'phone') return 0;
    if (tier.value === 'large-mobile') return RAIL_WIDTH_COLLAPSED;
    return filters.data.navExpanded ? RAIL_WIDTH_EXPANDED : RAIL_WIDTH_COLLAPSED;
}

/** The home-indicator / gesture-strip inset, in px. 0 where there is none. */
function readSafeAreaBottomPx(): number {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom');
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
}

function computeMiniBounds() {
    // Atlas Phase 6 / mobile-foldable-redesign §2.2 — the docked bar has two
    // shapes, and which one is a question about the TIER, not about a width:
    //
    //   phone        the fixed BottomTabBar owns the bottom of the viewport, so
    //                the bar docks ABOVE it (bar height + safe-area inset).
    //   large-mobile no tab bar (the rail took over) — the bar sits on the
    //                bottom edge of the content column, i.e. shifted right by
    //                the rail and with no bottom reserve to clear.
    //   desktop      free-dragging corner tile; not a bar at all.
    const w = filters.data.miniPlayerWidth;
    const h = filters.data.miniPlayerHeight;
    if (tier.value === 'phone') {
        const bottomReserve = BOTTOM_NAV_PX + readSafeAreaBottomPx();
        // Inset fixed media row above the bottom nav — not a free corner tile.
        // The touch tiers ignore any desktop drag position.
        return {
            top: Math.max(0, window.innerHeight - MINI_BAR_HEIGHT - bottomReserve - MINI_BAR_NAV_GAP),
            left: MINI_BAR_MARGIN_X,
            width: Math.max(0, window.innerWidth - MINI_BAR_MARGIN_X * 2),
            height: MINI_BAR_HEIGHT,
        };
    }
    if (tier.value === 'large-mobile') {
        const rail = railReservePx();
        // Same `viewport-fit=cover` reasoning as the phone branch: there is no
        // tab bar to clear here, but on a gesture-nav Android the bar's lower
        // edge would otherwise sit inside the system gesture strip.
        const safeBottom = readSafeAreaBottomPx();
        return {
            top: Math.max(0, window.innerHeight - MINI_BAR_HEIGHT - MINI_BAR_NAV_GAP - safeBottom),
            left: rail + MINI_BAR_MARGIN_X,
            width: Math.max(0, window.innerWidth - rail - MINI_BAR_MARGIN_X * 2),
            height: MINI_BAR_HEIGHT,
        };
    }
    // Desktop: default bottom-right, OR the user's dragged position (clamped to
    // the viewport so it can never be dragged off-screen).
    if (miniPos.value) {
        return {
            top: clampMiniTop(miniPos.value.top, h),
            left: clampMiniLeft(miniPos.value.left, w),
            width: w,
            height: h,
        };
    }
    return {
        top: Math.max(MINI_MARGIN, window.innerHeight - h - MINI_MARGIN),
        left: Math.max(MINI_MARGIN, window.innerWidth - w - MINI_MARGIN),
        width: w,
        height: h,
    };
}

function clampMiniLeft(left: number, w: number): number {
    return Math.max(MINI_MARGIN + railReservePx(), Math.min(left, window.innerWidth - w - MINI_MARGIN));
}
function clampMiniTop(top: number, h: number): number {
    return Math.max(MINI_MARGIN, Math.min(top, window.innerHeight - h - MINI_MARGIN));
}

function applyLiveBounds() {
    if (!liveTargetEl) return;
    const r = liveTargetEl.getBoundingClientRect();
    // If the target collapsed (route in transition / display:none parent)
    // skip — keep the prior bounds so the transition has somewhere to
    // animate from when we land back on /live.
    if (r.width <= 0 || r.height <= 0) return;
    playerBounds.value = { top: r.top, left: r.left, width: r.width, height: r.height };
}

function attachLiveObserver() {
    detachLiveObserver();
    const el = document.getElementById('player-mount-live');
    if (!el) return;
    liveTargetEl = el;
    applyLiveBounds();
    if (typeof ResizeObserver !== 'undefined') {
        liveResizeObserver = new ResizeObserver(() => applyLiveBounds());
        liveResizeObserver.observe(el);
    }
}

function detachLiveObserver() {
    liveResizeObserver?.disconnect();
    liveResizeObserver = null;
    liveTargetEl = null;
}

function applyBoundsForRoute() {
    if (isLiveRoute.value) {
        // Wait a frame for Live.vue's <RouterView> to mount its placeholder.
        // The defensive rAF poll handles slow router transitions.
        let tries = 0;
        const tryAttach = () => {
            tries += 1;
            const el = document.getElementById('player-mount-live');
            if (el) {
                attachLiveObserver();
                return;
            }
            if (tries < 6) requestAnimationFrame(tryAttach);
        };
        tryAttach();
    } else {
        detachLiveObserver();
        playerBounds.value = computeMiniBounds();
    }
}

watch(isLiveRoute, () => {
    void nextTick().then(applyBoundsForRoute);
}, { immediate: true });

watch(showPlayer, () => {
    void nextTick().then(applyBoundsForRoute);
});

// A tier change moves the docked bar between three different geometries, and it
// does NOT arrive with the resize event: `matchMedia`'s change listener and the
// window `resize` listener are independent, so `onWindowResize` can run with the
// tier still on its previous value and leave the bar at the old tier's rect
// until the next resize pixel. Recompute on the tier itself as well. `nextTick`
// so the rail's own width flip is in the DOM before the reserve is measured.
watch(tier, () => {
    void nextTick().then(applyBoundsForRoute);
});

watch(
    () => [filters.data.miniPlayerWidth, filters.data.miniPlayerHeight] as const,
    () => {
        if (!isLiveRoute.value) playerBounds.value = computeMiniBounds();
    },
);

// Toggling the sidebar (collapsed ↔ expanded) changes the desktop left reserve,
// so an off-/live mini tile must re-clamp clear of the wider rail. On /live the
// ResizeObserver on #player-mount-live handles the reflow (no reparent).
watch(
    () => filters.data.navExpanded,
    () => {
        if (!isLiveRoute.value) playerBounds.value = computeMiniBounds();
    },
);

function onWindowResize() {
    // The tier maintains itself (matchMedia); only the pixel geometry has to be
    // recomputed here.
    if (isLiveRoute.value) applyLiveBounds();
    else playerBounds.value = computeMiniBounds();
}

// ---------------------------------------------------------------
// Resize grip — drag the top-left corner of the mini tile.
// Pointer-based so touch + pen work. setPointerCapture on the
// grip ensures pointermove keeps firing if the cursor leaves the
// element during a fast drag.
// ---------------------------------------------------------------

interface ResizeState {
    startX: number;
    startY: number;
    startW: number;
    startH: number;
    pointerId: number;
}
let resizeState: ResizeState | null = null;

function onResizeDown(e: PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);
    resizeState = {
        startX: e.clientX,
        startY: e.clientY,
        startW: filters.data.miniPlayerWidth,
        startH: filters.data.miniPlayerHeight,
        pointerId: e.pointerId,
    };
    target.addEventListener('pointermove', onResizeMove);
    target.addEventListener('pointerup', onResizeUp);
    target.addEventListener('pointercancel', onResizeUp);
}

function onResizeMove(e: PointerEvent) {
    if (!resizeState) return;
    // Anchored bottom-right: dragging UP/LEFT enlarges the tile, so the
    // size delta is the inverse of the pointer delta.
    const dw = resizeState.startX - e.clientX;
    const dh = resizeState.startY - e.clientY;
    const { w, h } = clampMiniPlayer(resizeState.startW + dw, resizeState.startH + dh);
    filters.patch({ miniPlayerWidth: w, miniPlayerHeight: h });
}

function onResizeUp(e: PointerEvent) {
    if (!resizeState) return;
    const target = e.currentTarget as HTMLElement;
    try { target.releasePointerCapture(resizeState.pointerId); } catch { /* fine */ }
    target.removeEventListener('pointermove', onResizeMove);
    target.removeEventListener('pointerup', onResizeUp);
    target.removeEventListener('pointercancel', onResizeUp);
    resizeState = null;
}

// Field sweep B6 — a playback error belongs to the channel it happened on.
// Tuning away (a hop, or the auto-advance stepping to the next candidate)
// retires it, so the stack can never accumulate one card per channel visited.
watch(
    () => {
        const ch = playerStore.currentChannel;
        return ch ? `${ch.sourceId}:${ch.id}` : '';
    },
    (next, prev) => {
        if (next !== prev) retirePlaybackErrorToasts();
    },
);

// ---------------------------------------------------------------
// /live ?source&channel deep link sync.
// When the user picks a channel, reflect it in the URL so the
// page is bookmarkable / shareable. router.replace (not push) so
// the browser back-stack doesn't fill with a step per channel.
// ---------------------------------------------------------------
watch(
    () => playerStore.currentChannel,
    (ch) => {
        if (!ch) return;
        if (route.path !== '/live') return;
        const want = { source: String(ch.sourceId), channel: ch.id };
        const have = route.query;
        if (have.source === want.source && have.channel === want.channel) return;
        router.replace({ path: '/live', query: { ...have, ...want } }).catch(() => {});
    },
);

function expandFromMini(): void {
    if (!isLiveRoute.value) {
        const ch = playerStore.currentChannel;
        if (ch) {
            router.push({ path: '/live', query: { source: String(ch.sourceId), channel: ch.id } });
        } else {
            router.push('/live');
        }
    }
}

async function closeMini(): Promise<void> {
    await playerStore.closePlayer();
}

// X5 review #1/#2 — the per-session server caches are tied to the AUTH
// LIFECYCLE, not to a once-only boot check. A session that STARTS at the
// login screen finishes login via a client-side router.replace, so an
// `if (auth.isAuthed)` inside onMounted never re-runs — the preferences
// (and settings) stores stayed empty for the entire session and the whole
// prefs layer was inert (settings survived only via its lazy per-view
// rescues). And a logout→login user switch in the same tab kept the
// PREVIOUS user's preferences in the store — served to the new user and
// written onto their record by the first volume write-back. isAuthed
// flipping true loads both stores; flipping false resets the per-user one.
watch(
    () => auth.isAuthed,
    (authed) => {
        if (authed) {
            // Settings drive the VOD URL resolver's auto-transcode branch
            // (useVodStreamUrl Priority 0). Without this eager load, the
            // resolver sees `autoTranscode === undefined` on a cold
            // deep-link to /watch and falls through to Priority 5 (raw
            // direct), which fails on MKV / AVI / HEVC.
            settingsStore.refresh().catch((err: Error) => {
                log.warn('app.settings_boot_refresh_failed', { error: err.message });
            });
            // X5 (ux-remediation U4) — per-user preferences, loaded for
            // EVERY role (`/api/me/preferences` has no admin gate). Fire-
            // and-forget: consumers are reactive (the player's volume
            // attribute, key-binding predicates) and pick the data up when
            // it lands.
            prefsStore.refresh().catch((err: Error) => {
                log.warn('app.preferences_boot_refresh_failed', { error: err.message });
            });
        } else {
            prefsStore.reset();
            // Spec 10 — the notifier is per-user state living in a
            // module-level singleton, so it belongs in this branch with the
            // preferences store. Today logout hard-navigates and the document
            // dies with its timers; this keeps that from being load-bearing if
            // logout ever becomes a client-side route change.
            stopEpgNotificationScheduler();
        }
    },
);

onMounted(async () => {
    // Initial lazy navigation can still be resolving when App mounts. Preserve
    // the requested path (including a TV code) before deciding where to sign in.
    await router.isReady();
    // This mount owns the listener even when the initial protected deep link
    // redirects to login. Later sign-in/navigation does not remount App.
    window.addEventListener('resize', onWindowResize);
    // First-load whoami — the router guard returns undefined until auth.loaded
    // is true (so we don't bounce on the initial nav before Pinia has had a
    // chance to fetch /api/auth/whoami). After this completes, subsequent
    // navigations are gated by the auth guard in router/index.ts.
    await auth.refresh();
    if (!auth.isAuthed && !isPublicRoute.value) {
        log.info('app.unauthed_redirect_login', { from: route.name });
        router.replace({ path: '/login', query: { next: route.fullPath } });
        return;
    }
    if (auth.isAuthed && route.name === 'login') {
        await router.replace(loginDestination(route.query.next));
    }

    applyBoundsForRoute();
    if (isStandaloneRoute.value) return;

    // Resume the previously-watched channel after page reload. URL deep-link
    // (?source=&channel= on /live) wins because it's explicit user intent
    // (bookmark click); otherwise fall back to filters.lastChannel.
    const fromUrl = isLiveRoute.value
        ? { source: Number(route.query.source ?? NaN), channelId: String(route.query.channel ?? '') }
        : null;
    if (fromUrl && Number.isFinite(fromUrl.source) && fromUrl.channelId) {
        // Live.vue's deep-link consumer already handles this path on its mount.
        // Nothing to do here — Live.vue runs `consumeDeepLink()` in onMounted.
    } else if (filters.data.lastChannel && playerStore.currentChannel === null) {
        const last = filters.data.lastChannel;
        try {
            if (channelStore.channels.length === 0) {
                await channelStore.loadChannels();
            }
            const ch = channelStore.findChannel(last.sourceId, last.channelId);
            if (ch) {
                await playerStore.selectChannel(ch);
                log.info('app.player_resumed', { sourceId: last.sourceId, channelId: last.channelId });
            } else {
                log.warn('app.player_resume_channel_missing', last);
            }
        } catch (err) {
            log.warn('app.player_resume_failed', { error: (err as Error).message });
        }
    }
});

onBeforeUnmount(() => {
    void playerStore.stopPlayback('nodecast', 'app_unmounted');
    window.removeEventListener('resize', onWindowResize);
    detachLiveObserver();
});
</script>

<template>
    <div class="shell" :class="{ 'shell-rail': showRail }" :style="{ '--pip-reserve-bottom': pipReserveBottom + 'px' }">
        <AppRail v-if="showRail" />
        <BottomTabBar v-if="!isStandaloneRoute" />
        <main class="main" :class="{ public: isStandaloneRoute, 'full-bleed': isFullBleedRoute }">
            <RouterView />
        </main>
        <ToastStack />
        <ConfirmDialogHost />
        <!-- Mounted here so its "?" binding is the BOTTOM layer of the
             keybinding stack: any page or dialog layer shadows it, which is
             what we want if a surface ever needs "?" for itself. -->
        <KeyboardShortcuts v-if="!isStandaloneRoute" />

        <!-- Global player wrapper. Fixed-positioned; bounds are written
             by the ResizeObserver / window-resize handlers above. The
             DOM tree of <NodecastPlayer> never moves, so Vidstack's
             web-component lifecycle never fires disconnectedCallback —
             playback stays alive across route transitions. -->
        <div
            v-if="showPlayer"
            class="global-player-wrap"
            :class="{ mini: isMini, 'mini-bar': isMiniBar }"
            :style="{
                top: playerBounds.top + 'px',
                left: playerBounds.left + 'px',
                width: playerBounds.width + 'px',
                height: playerBounds.height + 'px',
            }"
        >
            <NodecastPlayer
                class="global-player"
                :channel="playerStore.currentChannel"
                :stream-result="playerStore.streamResult"
                :mini="isMini"
                :mini-bar="isMiniBar"
                :terminal-message="livePlaybackFailure"
                :terminal-retry-available="livePlaybackRetryAvailable"
                @playback-error="onLivePlaybackError"
                @playback-retry="retryLivePlayback"
            />
            <!-- Mini-only chrome: drag handle (top strip), resize grip (top-left),
                 expand + close (top-right). Drag + resize are desktop-only — the
                 mobile dock is a fixed bar (no free-drag/resize) per open-q #6. -->
            <div
                v-if="isMini && !isMiniBar"
                class="mini-drag-handle"
                aria-label="Move player"
                title="Drag to move"
                @pointerdown="onMiniDragDown"
            ></div>
            <div
                v-if="isMini && !isMiniBar"
                class="mini-resize-grip"
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize player"
                title="Drag to resize"
                @pointerdown="onResizeDown"
            ></div>
            <button
                v-if="isMini"
                type="button"
                class="mini-btn mini-expand"
                aria-label="Expand to full Live page"
                title="Open Live"
                @click="expandFromMini"
            >
                ⬈
            </button>
            <button
                v-if="isMini"
                type="button"
                class="mini-btn mini-close"
                aria-label="Stop playback"
                title="Close player"
                @click="closeMini"
            >
                ✕
            </button>
        </div>
    </div>
</template>

<style scoped>
.shell {
    /* Flex shell. Column (just <main>) on mobile — the BottomTabBar is fixed, so
       nothing else is in flow. `.shell-rail` flips to a row (AppRail sidebar +
       main) on every desktop route. The global player wrap, toasts, confirm host
       + PWA banner are all position:fixed / teleported, so they stay out of flex
       flow in both modes. */
    display: flex;
    flex-direction: column;
    /* `height` (not `min-height`) caps the shell at the viewport so an
       intrinsically tall child — e.g. the channel-list virtualiser's
       internal sizer at row_count × row_height — can't push the main pane
       to fit content. With `min-height: 100vh` and a 53k-channel
       account, `<main>` was expanding to ~3M px, which made
       Vidstack's `<media-player height: 100%>` measure itself at
       10,088 px and pushed `.watch-controls` far below the fold. */
    height: 100vh;
    overflow-x: hidden;
}

.shell-rail {
    flex-direction: row;
}

.main {
    flex: 1;
    padding: var(--space-sm);
    /* `min-height: 0` is the standard "let me actually shrink to my
       grid track" override — without it, `<main>` reports its
       min-content height (which includes overflow-hidden children's
       intrinsic content) and the 1fr row expands. Pages like /live
       want the row's set height, not main's natural height. */
    min-height: 0;
    min-width: 0;
    overflow-x: hidden;
    overflow-y: auto;
}

.main.public {
    padding: 0;
}

/* The padded column starts at the large-mobile tier — a 752px foldable has the
   room, and this used to wait until 768 (mobile-foldable-redesign §2.2). */
@media (min-width: 640px) { /* --bp-sm */
    .main {
        padding: var(--space-lg);
        /* Plus the notch, which the flat 24px only covers by coincidence. Same
           reasoning as `.main.full-bleed` below: a device fact, invisible to
           every test, and 0 where there is no notch. */
        padding-top: calc(var(--space-lg) + var(--safe-area-inset-top));
    }
}

@media (max-width: 639px) { /* --bp-sm */
    /* Clear the fixed BottomTabBar so the last row of scrollable content
       isn't hidden behind it. The tab bar exists ONLY on the phone tier, so
       this reserve moves with it — at 640 the rail has taken over and there is
       nothing at the bottom edge to clear. */
    .main {
        /* With the top NavBar gone, <main> starts at viewport y=0 on the phone —
           reserve the notch/status-bar inset so page content isn't clipped in
           the standalone PWA. */
        padding-top: calc(var(--space-sm) + var(--safe-area-inset-top));
        padding-bottom: calc(var(--space-sm) + var(--bottom-nav-height));
    }
}

/* Full-bleed canvas routes (Live, Guide) below 1024: no side padding, so the
   stage/list split and the EPG lane get the whole content column. The phone
   keeps the two reserves that are not padding-for-looks but clearance for fixed
   chrome — the notch inset and the tab bar. Desktop is untouched. */
@media (max-width: 1023px) { /* --bp-xl */
    .main.full-bleed {
        padding-left: 0;
        padding-right: 0;
        /* The notch inset is a DEVICE fact, not a tier fact: `viewport-fit=cover`
           + `display: standalone` means `env(safe-area-inset-top)` is live in the
           installed PWA at every width, and a foldable has one. Before M2 the
           `<= 896px` block gave 752 this reserve; narrowing that block to 639
           dropped it, and no test can see it (Chromium reports 0 for every
           `env()` inset). It resolves to 0 on a device without a notch, so the
           only cost of keeping it is a line. */
        padding-top: var(--safe-area-inset-top);
        /* Zeroed EXPLICITLY. The `(min-width: 640px)` `.main` rule above is a
           `padding` SHORTHAND, so it supplies 24px here that this block's
           three longhands never override — and `.live-page` is `height: 100%`
           of the content box, so at 840×360 the whole Live grid was 336px
           instead of 360. The plan's own worst-case list budget (360 − 52 − 44
           = 264 against a ≥ 240 gate) was passing with zero slack because of
           it. The phone re-adds its tab-bar reserve below. */
        padding-bottom: 0;
    }
}

@media (max-width: 639px) { /* --bp-sm */
    .main.full-bleed {
        /* The phone keeps the one reserve that is clearance for fixed chrome
           rather than decoration: the BottomTabBar. (The notch inset is set for
           the whole touch range above.) */
        padding-bottom: var(--bottom-nav-height);
    }
}

/*
 * Global player wrapper. position: fixed at all times, with bounds
 * written via inline style from `playerBounds`. The CSS transition
 * is what gives the slide-in / slide-out animation between full-/live
 * mode and mini-corner mode — no DOM moves, so Vidstack's
 * `disconnectedCallback` never fires and playback continues.
 *
 * `prefers-reduced-motion: reduce` strips the transition.
 */
.global-player-wrap {
    position: fixed;
    z-index: var(--z-base, 1);
    background: var(--bg-deep, #000);
    overflow: hidden;
    transition:
        top 280ms ease,
        left 280ms ease,
        width 280ms ease,
        height 280ms ease,
        border-radius 280ms ease,
        box-shadow 280ms ease;
}
.global-player-wrap.mini {
    z-index: var(--z-toast, 9000);
    border-radius: var(--radius-md);
    box-shadow: 0 12px 36px rgba(0, 0, 0, 0.45);
}
/* Atlas Phase 7 (open-q #6) — mobile docked mini-bar: full-bleed strip above
   the bottom nav, square top edge, shadow only upward. The video is cropped to
   fill the short bar (cover) since the bar is audio-first; tap ⬈ to go full. */
.global-player-wrap.mini-bar {
    border: 1px solid var(--color-border);
    border-radius: 11px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
}
.global-player {
    width: 100%;
    height: 100%;
}

@media (max-width: 1023px) /* --bp-xl */ {
    /* Touch-tier route changes, rotations and unfolds must SNAP to the Live
       mount. A bounds transition can leave the fixed player covering the
       viewport at its previous docked-bar geometry while the page is already
       interactive — and a foldable changes its viewport more often than a phone
       does, which is why this now covers the whole touch range, not just ≤896. */
    .global-player-wrap {
        transition: none;
    }
}

.mini-btn {
    position: absolute;
    top: var(--space-xs);
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 0;
    background: rgba(0, 0, 0, 0.55);
    color: var(--text);
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    /* Stack above the player (which sits at var(--z-toast) in mini mode). */
    z-index: calc(var(--z-toast) + 1);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 150ms ease;
}
.mini-btn:hover {
    background: rgba(0, 0, 0, 0.78);
}
.mini-expand {
    right: calc(var(--space-xs) + 36px);
}
.mini-close {
    right: var(--space-xs);
}
.global-player-wrap.mini-bar .mini-btn {
    top: 11px;
    width: 32px;
    height: 32px;
    background: transparent;
    color: rgba(255, 255, 255, 0.8);
}
.global-player-wrap.mini-bar .mini-expand {
    right: 38px;
}
.global-player-wrap.mini-bar .mini-close {
    right: 4px;
}

/* Atlas Phase 6 — desktop PIP drag handle: a top strip between the resize grip
   (top-left) and the expand/close buttons (top-right). Lower z than both so a
   click on a control always wins over starting a drag. */
.mini-drag-handle {
    position: absolute;
    top: 0;
    left: 18px;
    right: 76px;
    height: 22px;
    cursor: grab;
    z-index: var(--z-toast);
    touch-action: none;
}
.mini-drag-handle:active {
    cursor: grabbing;
}

.mini-resize-grip {
    position: absolute;
    top: 0;
    left: 0;
    width: 18px;
    height: 18px;
    cursor: nwse-resize;
    /* One step above the chrome buttons so a drag started on the corner
       wins over a hover on the close/expand affordance. */
    z-index: calc(var(--z-toast) + 2);
    /* Visible chevron via CSS so the grip is discoverable. */
    background:
        linear-gradient(135deg, transparent 0 35%, rgba(255, 255, 255, 0.6) 35% 45%, transparent 45% 55%, rgba(255, 255, 255, 0.6) 55% 65%, transparent 65%);
    background-size: 100% 100%;
    touch-action: none; /* prevent browser pan during pointer drag */
}
.mini-resize-grip:hover {
    background:
        linear-gradient(135deg, transparent 0 35%, rgba(255, 255, 255, 0.9) 35% 45%, transparent 45% 55%, rgba(255, 255, 255, 0.9) 55% 65%, transparent 65%);
}

@media (prefers-reduced-motion: reduce) /* feature: reduced-motion */ {
    .global-player-wrap {
        transition: none;
    }
}
</style>
